/* eslint-disable camelcase */
import React, { useCallback, useMemo, useRef } from "react"
import * as yup from "yup"
import _ from "lodash"
import moment from "moment"
import { Appearance, TextInput } from "react-native"
import { Country, Currency, Region } from "../../../database"
import { Formik } from "formik"
import { FormProps } from "../form"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { ModelField } from "../../field/model-field/model-field"
import { TextField } from "../../field/text-field/text-field"
import { PickerField } from "../../field/picker-field/picker-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { color } from "../../../theme"

const timeZone = [
  { time: '-12:00', city: 'Eniwetok, Kwajalein' },
  { time: '-11:00', city: 'Midway Island, Samoa' },
  { time: '-10:00', city: 'Hawaii' },
  { time: '-09:00', city: 'Alaska' },
  { time: '-08:00', city: 'Pasific Time (US &amp; Canada)' },
  { time: '-07:00', city: 'Mountain Time (US &amp; Canada' },
  { time: '-06:00', city: 'Central Time (US &amp; Canada), Mexico City' },
  { time: '-05:00', city: 'Eastern Time (US &amp; Canada), Bogota, Lima' },
  { time: '-04:00', city: 'Atlantic Time (Canada), Caracas, La Paz' },
  { time: '-03:30', city: 'Newfoundland' },
  { time: '-03:00', city: 'Brazil, Buenos Aires, Georgetown' },
  { time: '-02:00', city: 'Mid-Atlantic' },
  { time: '-01:00', city: 'Azores, Cape Verde Islands' },
  { time: '00:00', city: 'Western Europe Time, London, Lisbon, Casablanca' },
  { time: '+01:00', city: 'Brussels, Copenhagen, Madrid, Paris' },
  { time: '+02:00', city: 'Kaliningrad, South Africa' },
  { time: '+03:00', city: 'Baghdad, Riyadh, Moscow, St. Petersburg' },
  { time: '+03:30', city: 'Tehran' },
  { time: '+04:00', city: 'Abu Dhabi, Muscat, baku, Tbilisi' },
  { time: '+04:30', city: 'Kabul' },
  { time: '+05:00', city: 'Ekaterinburg, Islambad, Karachi, Tashkent' },
  { time: '+05:30', city: 'Bombay, Calcutta, Madras, New Delhi' },
  { time: '+05:45', city: 'Kathmandu' },
  { time: '+06:00', city: 'Almaty, Dhaka, Colombo' },
  { time: '+07:00', city: 'Bangkok, Hanoi, Jakarta' },
  { time: '+08:00', city: 'Beijing, Perth, Singapore, Hong Kong' },
  { time: '+09:00', city: 'Tokyo, Seoul, Osaka, Sapporo, Yakutsk' },
  { time: '+09:30', city: 'Adelaide, Darwin' },
  { time: '+10:00', city: 'Eastern Australia, Guam, Vladivostok' },
  { time: '+11:00', city: 'Magadan, Solomon Islands, New Caledonia' },
  { time: '+12:00', city: 'Auckland, Wellington, Fiji, Kamchatka' }
];

export interface FarmFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    id?: number
    farm_id?: number
    name?: string
    country_id?: string
    region_id?: string
    province_id?: string
    regency_id?: string
    district_id?: string
    village_id?: string
    address?:string
    currency_id?: string
    area?: number
  }
}

/**
 * Describe your component here
 */
export const FarmForm = observer(function FarmForm(props: FarmFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = "light" || Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props;

  const input = {
    name: useRef<TextInput>(),
    area: useRef<TextInput>(),
    address: useRef<TextInput>(),
  }

  const nextInput = {
    name: () => input.area.current && input.area.current.focus(),
  }

  const validationSchema = yup.object({
    name: yup.string().ascii().nullable().required().label(translate("farmForm.name")),
    area: yup.number().nullable().label(translate("farmForm.area")),
    country_id: yup.string().nullable().required().label(translate("farmForm.countryId")),
    province_id: yup.string().nullable().required().label(translate("farmForm.provinceId")),
    regency_id: yup.string().nullable().required().label(translate("farmForm.regencyId")),
    district_id: yup.string().nullable().required().label(translate("farmForm.districtId")),
    village_id: yup.string().nullable().required().label(translate("farmForm.villageId")),
    currency_id: yup.string().nullable().required().label(translate("farmForm.currencyId")),
    timezone: yup.string().nullable().required().label(translate("farmForm.timezone"))
  })

  const onSubmit = useCallback(async (values) => {
    const { province_id, regency_id, district_id, village_id, ...restValues } = values
    if (village_id) {
      await Region.syncAll({ id__in: village_id }, [['regions.id', [village_id]]])
    }    
    onSubmitProps({
      ...restValues,
      region_id: village_id || district_id || regency_id || province_id
    })
  }, [onSubmitProps])

  const initialValues = useMemo<FarmFormProps["initialValues"]>(() => ({
    name: "",
    country_id: "ID",
    province_id: null,
    regency_id: null,
    district_id: null,
    village_id: null,
    address: null,
    area: null,
    currency_id: "",
    timezone: timeZone.find(({ time }) => time === moment().format('Z')).time,
    ...initialValuesProps
  }), [initialValuesProps])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue }) => (<>
        <TextField
          name="name"
          txPrefix="farmForm"
          colorScheme={colorScheme}
          input={input}
          nextInput={nextInput}
          formControlProps={{ isRequired: true }}
        />
        {initialValues.name !== "" && <NumeralField
            name="area"
            txPrefix="farmForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
        />}
        <ModelField
          name="country_id"
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          modelPickerProps={{
            Model: Country,
            titleTx: "farmForm.countryIdPlaceholder",
            fetchData: (model, search) => (
              model.fetch({
                search,
                sort: 'name'
              }).then((models) => (
                _.sortBy(models, (m) => {
                  const index = ['ID', 'EC', 'VN'].findIndex((k) => k === m.id)
                  return index < 0 ? m.name : `${index}`
                })
              ))
            ),
            onValueChange(value) {
              setFieldValue("country_id", value)
              setFieldValue("province_id", null, false)
              setFieldValue("regency_id", null, false)
              setFieldValue("district_id", null, false)
              setFieldValue("village_id", null, false)
            },
          }}
        />
        <ModelField
          name="province_id"
          key={`${values.country_id}-province`}
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          modelPickerProps={{
            Model: Region,
            dataLabel: "full_name",
            titleTx: "farmForm.provinceId",
            value: values.province_id,
            fetchData: (model, search) => {
              return (
                model.fetch({
                  country_id__eq: values.country_id,
                  scope_only: 'province',
                  name: search,
                  sort: 'name',
                }).then(models => {
                  if (values.country_id !== 'ID')
                    return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                  return models
                })
              )
            },
            onValueChange(value) {
              setFieldValue("province_id", value)
              if (value === "99") {
                setFieldValue("regency_id", value)
                setFieldValue("district_id", value)
                setFieldValue("village_id", value)
              } else {
                setFieldValue("regency_id", null, false)
                setFieldValue("district_id", null, false)
                setFieldValue("village_id", null, false)
              }
            },
            isDisabled: (!values.country_id)
          }}
        />
        <ModelField
          name="regency_id"
          key={`${values.province_id}-regency`}
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          modelPickerProps={{
            Model: Region,
            dataLabel: "name",
            titleTx: "farmForm.regencyId",
            value: values.regency_id,
            fetchData: (model, search) => {
              return (
                model.fetch({
                  province_id__eq: values.province_id,
                  scope_only: 'regency',
                  name: search,
                  sort: 'name',
                }).then(models => {
                  if (values.country_id !== 'ID')
                    return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                  return models
                })
              )
            },
            onValueChange(value) {
              setFieldValue("regency_id", value)
              if (value === "99") {
                setFieldValue("district_id", value)
                setFieldValue("village_id", value)
              } else {
                setFieldValue("district_id", null, false)
                setFieldValue("village_id", null, false)
              }
            },
            isDisabled: (values.province_id === "99" || !values.province_id || !values.country_id)
          }}
        />
        <ModelField
          name="district_id"
          key={`${values.regency_id}-district`}
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          modelPickerProps={{
            Model: Region,
            dataLabel: "name",
            titleTx: "farmForm.districtId",
            value: values.district_id,
            fetchData: (model, search) => {
              return (
                model.fetch({
                  regency_id__eq: values.regency_id,
                  scope_only: 'district',
                  search,
                  sort: 'name',
                }).then(models => {
                  if (values.country_id !== 'ID')
                    return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                  return models
                })
              )
            },
            onValueChange: async (value) => {
              setFieldValue("district_id", value)
              if (value === "99") {
                setFieldValue("village_id", value)
              } else {
                setFieldValue("village_id", null, false)
              }
            },
            isDisabled: (values.regency_id === "99" || !values.regency_id || !values.province_id || !values.country_id)
          }}
        />
        <ModelField
          key={`${values.district_id}-village`}
          name="village_id"
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          modelPickerProps={{
            Model: Region,
            dataLabel: "name",
            titleTx: "farmForm.villageId",
            value: values.village_id,
            fetchData: async (model, search) => {
              Region.online = true;
              return (
                model.fetch({
                  district_id__eq: values.district_id,
                  scope_only: 'village',
                  name: search,
                  sort: 'name',
                }).then(models => {
                  if (values.country_id !== 'ID')
                    return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                  return models
                }).finally(() => {
                  Region.online = false
                })
              )
            },
            onValueChange(value) {
              setFieldValue("village_id", value)
            },
            isDisabled: (values.district_id === "99" || !values.district_id
              || !values.regency_id || !values.province_id || !values.country_id)
          }}
        />
        <TextField
          key={`${values.village_id}-address`}
          name="address"
          txPrefix="farmForm"
          colorScheme={colorScheme}
          input={input}
          nextInput={nextInput}
          formControlProps={{
            isDisabled: (!values.village_id || !values.district_id
              || !values.regency_id || !values.province_id || !values.country_id),
            style: {
              paddingHorizontal: 16,
              marginBottom: 8,
              paddingBottom: 8,
              backgroundColor: color.palette.white
            },
            inputProps: {
              multiline: true,
              style: {
                height: 72
              }
            }
          }}
        />
        <ModelField
          name="currency_id"
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          modelPickerProps={{
            Model: Currency,
            titleTx: "farmForm.currencyIdPlaceholder",
            fetchData: (model, search) => (
              model.fetch({
                search,
                sort: 'name'
              }).then((models) => (
                _.sortBy(models, (m) => {
                  const index = ['IDR', 'USD', 'VND'].findIndex((k) => k === m.id)
                  return index < 0 ? m.name : `${index}`
                })
              ))
            ),
            renderSelectedItemText: (item) => item && `${item.name} (${item.id})`
          }}
        />
        <PickerField
          name="timezone"
          txPrefix="farmForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          pickerProps={{
            titleTx: "farmForm.timezonePlaceholder",
            data: timeZone,
            dataKey: "time",
            isUseSearch: false,
            renderSelectedItemText: (item) => item && `(GMT ${item.time}) ${item.city}`
          }}
        />
      </>)}
    </Formik>
  )
})

FarmForm.displayName = 'FarmForm'
